import { DateTime as DT } from 'luxon';
import type { TFunction } from 'i18next';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    flattenDetailsEntries,
    getObjectRecord,
    getObjectDelailsRaw,
    parseJsonObject,
    toSafeTemplateKeyName,
} from '../OffKeys/keysUtils';

export const CUSTOMER_TEMPLATE_KEYS = [
    'personType',
    'fullName',
    'edrpouOrIpn',
    'vatEnabled',
    'vatNumber',
    'iban',
    'bank',
    'phone',
    'email',
    'legalAddress',
    'representativeName',
    'representativePosition',
] as const;

export const CLIENT_TEMPLATE_KEYS = [
    'clientAddress',
    'clientCreated',
    'clientDescription',
    'clientEmail',
    'clientId',
    'clientLocation',
    'clientName',
    'clientPhone',
] as const;

export const GENERAL_TEMPLATE_KEYS = [
    'objectCreated',
    'objectDeadline',
    'objectDelails',
    'objectFinished',
    'objectInfo',
    'objectName',
    'objectPrice',
    'objectPriceRate',
    'objectStatus',
] as const;

const DATE_KEYS = new Set([
    'objectCreated',
    'objectDeadline',
    'objectFinished',
    'clientCreated',
]);

const PRICE_KEYS = new Set(['objectPrice', 'objectPriceRate']);

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const formatDateValue = (raw: unknown): string => {
    const millis = Number(raw || 0);
    if (!Number.isFinite(millis) || millis <= 0) return '';
    return DT.fromMillis(millis).toFormat('dd.MM.yyyy');
};

const formatScalarValue = (raw: unknown, key: string, tObjects?: TFunction): string => {
    if (raw === null || raw === undefined) return '';

    if (DATE_KEYS.has(key)) return formatDateValue(raw);

    if (typeof raw === 'boolean') {
        if (key === 'vatEnabled' && tObjects) {
            return raw ? tObjects('customer.fields.vatYes') : tObjects('customer.fields.vatNo');
        }
        return raw ? '1' : '0';
    }

    if (Array.isArray(raw)) {
        return escapeHtml(raw.map((item) => String(item ?? '').trim()).filter(Boolean).join(', '));
    }

    if (typeof raw === 'object') {
        try {
            return escapeHtml(JSON.stringify(raw));
        } catch (_e) {
            return '';
        }
    }

    if (PRICE_KEYS.has(key)) {
        const num = Number(raw);
        return Number.isFinite(num) ? formatPrice(num) : '';
    }

    if (key === 'personType' && tObjects) {
        const personType = String(raw || '').trim();
        if (personType === 'legal' || personType === 'individual') {
            return escapeHtml(tObjects(`customer.personTypes.${personType}`));
        }
    }

    if (key === 'objectInfo') {
        return String(raw || '');
    }

    return escapeHtml(String(raw));
};

const formatDetailsObject = (details: Record<string, unknown>): string =>
    flattenDetailsEntries(details)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return `${key}: ${value.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ')}`;
            }
            if (value === null || value === undefined) return `${key}:`;
            if (typeof value === 'object') {
                try {
                    return `${key}: ${JSON.stringify(value)}`;
                } catch (_e) {
                    return `${key}:`;
                }
            }
            return `${key}: ${String(value)}`;
        })
        .map(escapeHtml)
        .join('<br/>');

const setDetailKey = (map: Record<string, string>, key: string, value: string) => {
    const safeKey = String(key || '').trim();
    if (!safeKey) return;
    map[safeKey] = value;
    const underscored = toSafeTemplateKeyName(safeKey);
    if (underscored && underscored !== safeKey) {
        map[underscored] = value;
    }
};

export interface BuildObjectTemplateValueKeysParams {
    objectData?: unknown;
    tObjects?: TFunction;
}

/** Builds replaceable template keys from object / customer / client / specification data. */
export const buildObjectTemplateValueKeysMap = ({
    objectData,
    tObjects,
}: BuildObjectTemplateValueKeysParams): Record<string, string> => {
    const objectRecord = getObjectRecord(objectData);
    if (!objectRecord) return {};

    const map: Record<string, string> = {};

    const details = parseJsonObject(getObjectDelailsRaw(objectRecord));
    if (details) {
        flattenDetailsEntries(details).forEach(([key, value]) => {
            setDetailKey(map, key, formatScalarValue(value, key, tObjects));
        });
        map.objectDelails = formatDetailsObject(details);
    } else {
        const rawDetails = getObjectDelailsRaw(objectRecord);
        if (rawDetails != null && rawDetails !== '') {
            map.objectDelails = escapeHtml(String(rawDetails));
        }
    }

    const customer = parseJsonObject(objectRecord.objectCustomer);
    if (customer) {
        CUSTOMER_TEMPLATE_KEYS.forEach((key) => {
            map[key] = formatScalarValue(customer[key], key, tObjects);
        });
    }

    CLIENT_TEMPLATE_KEYS.forEach((key) => {
        map[key] = formatScalarValue(objectRecord[key], key, tObjects);
    });

    GENERAL_TEMPLATE_KEYS.forEach((key) => {
        if (key === 'objectDelails' && map.objectDelails !== undefined) return;
        map[key] = formatScalarValue(objectRecord[key], key, tObjects);
    });

    return map;
};
